/**
 * Post-register/login subscription status readiness helpers.
 * Fresh Community Edition users can briefly see no_subscription / empty plan_name
 * before the provisioned row is visible — retry instead of terminal empty dashboard.
 */
// ***** Code Start BH07092026_2150 - CE dashboard status retry (testable)

const DEFAULT_SUBSCRIPTION_STATUS_RETRY_DELAYS_MS = Object.freeze([0, 700, 1400, 2200]);

/**
 * True when status payload is ready to render on the dashboard.
 * Expired is ready (show expiry UI). Loading... / no_subscription / empty plan are not.
 */
function isSubscriptionStatusReady(subscriptionData) {
  if (!subscriptionData || typeof subscriptionData !== 'object') {
    return false;
  }
  const status = String(subscriptionData.status || '').toLowerCase();
  const planName = subscriptionData.plan_name;
  if (status === 'expired') {
    return true;
  }
  if (status === 'no_subscription') {
    return false;
  }
  if (!planName || planName === 'Loading...') {
    return false;
  }
  return true;
}

/**
 * Whether Dashboard should keep showing the loading progress screen.
 * Auth must be done; subscription still in flight keeps the progress UI.
 */
function shouldShowDashboardLoadingProgress({
  authContextLoading = false,
  authLoading = false,
  subscriptionLoading = false,
} = {}) {
  return Boolean(authContextLoading || authLoading || subscriptionLoading);
}

/**
 * Fetch subscription status with short retries for new CE provisioning races.
 * @param {() => Promise<object|null>} fetchStatus - usually apiService.getSubscriptionStatus(true)
 * @param {{ delaysMs?: number[], sleep?: (ms:number)=>Promise<void> }} [opts]
 */
async function fetchSubscriptionStatusWithRetry(fetchStatus, opts = {}) {
  if (typeof fetchStatus !== 'function') {
    throw new Error('fetchStatus function is required');
  }
  const delaysMs =
    Array.isArray(opts.delaysMs) && opts.delaysMs.length
      ? opts.delaysMs
      : DEFAULT_SUBSCRIPTION_STATUS_RETRY_DELAYS_MS;
  const sleep =
    typeof opts.sleep === 'function'
      ? opts.sleep
      : (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  let last = null;
  for (let i = 0; i < delaysMs.length; i += 1) {
    const delay = Number(delaysMs[i]) || 0;
    if (delay > 0) {
      await sleep(delay);
    }
    last = await fetchStatus();
    if (isSubscriptionStatusReady(last)) {
      return last;
    }
  }
  return last;
}

module.exports = {
  DEFAULT_SUBSCRIPTION_STATUS_RETRY_DELAYS_MS,
  isSubscriptionStatusReady,
  shouldShowDashboardLoadingProgress,
  fetchSubscriptionStatusWithRetry,
};

// ***** Code End BH07092026_2150
